/**
 * Role helpers from the logged-in user's profile (localStorage).
 * Field staff (technician / tester / maintenance team) get a personal
 * dashboard. Supervisors get the supervisor scorecard. Other higher
 * roles keep the plant dashboard.
 */

const FIELD_ROLE = /technician|tester|maintenance team/i;
const HIGHER_ROLE = /supervisor|manager|admin|director|head/i;
const SUPERVISOR_ROLE = /supervisor/i;
const ASSIGNER_ROLE = /supervisor|manager/i;

export const getUserRoleNames = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user_details") || "null");
    return (user?.employee_role || []).map((r) => r?.employee_role || "");
  } catch {
    return [];
  }
};

export const isHigherRoleUser = (roleNames = getUserRoleNames()) =>
  roleNames.some((name) => HIGHER_ROLE.test(name));

/** Same-stage ticket reassign is Supervisor only. */
export const isSupervisorUser = (roleNames = getUserRoleNames()) =>
  roleNames.some((name) => SUPERVISOR_ROLE.test(name));

/** Manager/Supervisor may assign or reassign a ticket to a present technician. */
export const isAssignerUser = (roleNames = getUserRoleNames()) =>
  roleNames.some((name) => ASSIGNER_ROLE.test(name));

export const isFieldRoleUser = (roleNames = getUserRoleNames()) =>
  roleNames.some((name) => FIELD_ROLE.test(name));

/** True when this login should see the personal technician dashboard. */
export const isTechnicianDashboardUser = () => {
  const roles = getUserRoleNames();
  return isFieldRoleUser(roles) && !isHigherRoleUser(roles);
};

export const readStoredAccessList = () => {
  const raw = localStorage.getItem("access_list_data");
  if (!raw || raw === "undefined" || raw === "null") return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/** First route this login can actually open. */
export const pickLandingPath = (accessList = readStoredAccessList()) => {
  if (!Array.isArray(accessList) || accessList.length === 0) {
    if (isTechnicianDashboardUser() || isSupervisorUser()) return "/dashboard";
    return "/no-access";
  }
  const has = (moduleName) =>
    Boolean(
      accessList.find(
        (item) => item.module_name === moduleName && item.read_access
      )
    );
  if (isTechnicianDashboardUser()) return "/dashboard";
  if (isSupervisorUser()) return "/dashboard";
  if (has("Tickets")) return "/tickets";
  if (has("Dashboard")) return "/dashboard";
  if (has("Assets")) return "/asset";
  if (has("Audit")) return "/audit";
  if (has("Checklist")) return "/checklist";
  if (has("Spares")) return "/spare-management";
  if (has("Spare Analytics")) return "/spare-analytics";
  if (has("Visitor Details")) return "/visitor-details";
  if (has("Report")) return "/report";
  return "/no-access";
};
